<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-btn v-if="canEdit" class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          {{ createText }}
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          hide-search-button
          @submit="onSearch"
        />
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="catalogs_tables"
            ref="data_table"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="fields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="fields" :rows="6"/>
            </template>

            <template #cell(name)="row">
              <slot name="name-tag" :item="row.item">
                {{ row.item.name }}
              </slot>
            </template>

            <template #cell(active)="row">
              <v-icon name="buttons/cross" v-if="!row.item.active" :size="13"/>
              <v-icon name="buttons/check" v-if="row.item.active" :size="14"/>
            </template>

            <template #cell(action)="row">
              <b-btn v-if="canEdit" variant="link" class="text-dark" @click="onEdit(row.item)">
                <v-icon name="buttons/edit" :size="15"/>
              </b-btn>
              <b-btn v-else variant="link" class="text-dark" @click="onView(row.item)">
                <v-icon name="buttons/eye" :size="15"/>
              </b-btn>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных записей в каталоге.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте новую запись прямо сейчас</p>
          <b-btn v-if="canEdit" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            {{ createText }}
          </b-btn>
        </div>
      </div>
    </div>

    <router-view
      :key="$route.fullPath"
      @refresh="getItems"
      @modal-closed="onModalClosed"
    />

  </div>
</template>

<script>
import helpers from "../../utilites/helpers";
import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
import pageSearchService from "../../utilites/pageSearchService";
import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";

export default {
  name: 'Catalogs',
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer },
  mixins: [ helpers, pageSearchService ],
  props: {
    title: {type: String, required: true},
    createText: {type: String, required: true},
    fetchData: {type: Function, required: true},
    fields: {type: Array, required: true},
    routeCreate: {type: String, required: true},
    routeEdit: {type: String, required: true},
    routeView: {type: String, required: true},
    routePath: {type: String, required: true},
    canEdit: { type: Boolean, required: true},
  },
  data() {
    return {
      items: [],
      isBusy: false,
      sortBy: 'id',
      sortDesc: true,
      filters: {
        query: '',
      },
      deleteRow: {
        id: null,
        name: ''
      },
    };
  },
  async created() {
    const query = this.$route.query;

    if (query && query.query) {
      this.filters.query = query.query;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.onSearch();
  },
  computed: {
    isFiltered() {
      return !!this.filters.query && this.filters.query.trim().length > 0;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.fetchData(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        this.items = [];
        this.loading = false;
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
    },
    onCreate() {
      this.$router.push({ name: this.routeCreate }).catch((e) => {
        if (e.name === "permissions") {
          this.showToast('У Вас нет доступа выполнить это действие.', 'Нет доступа', 'danger');
        }
      });
    },
    onRowClicked(item) {
      if (this.canEdit) {
        this.onEdit(item);
      } else {
        this.onView(item);
      }
    },
    onEdit(item) {
      this.$router.push({ name: this.routeEdit, params: { id: item.id }}).catch((e) => {
        if (e.name === "permissions") {
          this.showToast('У Вас нет доступа выполнить это действие.', 'Нет доступа', 'danger');
        }
      });
    },
    onView(item) {
      this.$router.push({ name: this.routeView, params: { id: item.id }}).catch((e) => {
        if (e.name === "permissions") {
          this.showToast('У Вас нет доступа выполнить это действие.', 'Нет доступа', 'danger');
        }
      });
    },
    onModalClosed() {
      this.$router.replace({
        path: this.routePath,
        query: this.$route.query,
        hash: this.$route.hash,
      }).catch(()=>{});
    },
  }
}
</script>

<style>
#catalogs_tables th {
  vertical-align: middle;
}

#catalogs_tables td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Catalogs/Catalogs.vue